import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { getShadowtrack } from '@racemap/utilities/functions/map';
import type { PredictionParams } from '@racemap/utilities/types/events';
import type { AcceptedReadRust, RejectedReadRust } from '@racemap/utilities/types/trackping';
import type { Reader } from '@racemap/utilities/types/types';
import { simplify } from '@turf/simplify';
import type { FeatureCollection, Point, Position } from 'geojson';
import { castDraft } from 'immer';
import type { FC } from 'react';
import type { MapMouseEvent } from 'react-map-gl/mapbox';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { getDefaultViewState } from '../../lib/map-helpers';
import { useStore } from '../../store/reducers';
import { BasicMap, Layers } from '../BasicComponents/Maps';
import { FocusPointLayer } from '../BasicComponents/Maps/Layers';
import { AUTOFOCUS } from '../BasicComponents/Maps/helpers';

type MappingPoint = Position;
type MappingLine = Array<Position>;

type Visuals = {
  acceptedReadsPositions: FeatureCollection<Point, AcceptedReadRust>;
  rejectedReadsPositions: FeatureCollection<Point, RejectedReadRust>;
  readerPositions: FeatureCollection<Point, Reader>;
  mappingPositions: Array<MappingPoint>;
  mappingLines: Array<MappingLine>;
};

function getGeoVisuals(
  readers: Array<Reader>,
  accepedReads: Array<AcceptedReadRust>,
  rejectedReads: Array<RejectedReadRust>,
): Visuals {
  const visuals: Visuals = {
    acceptedReadsPositions: { type: 'FeatureCollection', features: [] },
    rejectedReadsPositions: { type: 'FeatureCollection', features: [] },
    readerPositions: { type: 'FeatureCollection', features: [] },
    mappingPositions: [],
    mappingLines: [],
  };

  for (const accepedRead of accepedReads) {
    visuals.acceptedReadsPositions.features.push({
      type: 'Feature',
      geometry: {
        coordinates: [accepedRead.location.lng, accepedRead.location.lat],
        type: 'Point',
      },
      properties: accepedRead,
    });

    if (accepedRead.mapping != null) {
      visuals.mappingPositions.push([accepedRead.mapping.lng, accepedRead.mapping.lat]);
      visuals.mappingLines.push([
        [accepedRead.location.lng, accepedRead.location.lat],
        [accepedRead.mapping.lng, accepedRead.mapping.lat],
      ]);
    }
  }

  for (const rejectedRead of rejectedReads) {
    if (rejectedRead.location.lat == null || rejectedRead.location.lng == null) continue;
    visuals.rejectedReadsPositions.features.push({
      type: 'Feature',
      geometry: {
        coordinates: [rejectedRead.location.lng, rejectedRead.location.lat],
        type: 'Point',
      },
      properties: { ...rejectedRead, readerId: rejectedRead.readerId },
    });
  }

  for (const reader of readers) {
    if (reader.location == null) continue;

    visuals.readerPositions.features.push({
      type: 'Feature',
      geometry: { coordinates: [reader.location.lng, reader.location.lat], type: 'Point' },
      properties: { ...reader },
    });

    if (Array.isArray(reader.mappings)) {
      for (const mapping of reader.mappings) {
        visuals.mappingPositions.push([mapping.lng, mapping.lat]);
        visuals.mappingLines.push([
          [reader.location.lng, reader.location.lat],
          [mapping.lng, mapping.lat],
        ]);
      }
    }
  }

  return visuals;
}

interface ReaderMapProps {
  readers?: Array<Reader>;
  readerInFocus?: Reader | null;
  hoveredPosition: number | null;
  predictionParams: PredictionParams | null;
  acceptedReads?: Array<AcceptedReadRust>;
  acceptedReadInFocus?: AcceptedReadRust | null;
  rejectedReads?: Array<RejectedReadRust>;
  rejectedReadInFocus?: RejectedReadRust | null;
  positionToMoveTo?: {
    lat: number;
    lng: number;
  } | null;
  showLegend?: boolean;
  showRejectedReads?: boolean;
  onReaderInFocusChange?: (reader: Reader | null) => void;
  onRejectedReadInFocusChange?: (rejectedRead: RejectedReadRust | null) => void;
  onAcceptedReadInFocusChange?: (acceptedRead: AcceptedReadRust | null) => void;
}

export const ReadersMap: FC<ReaderMapProps> = ({
  readers = [],
  readerInFocus,
  hoveredPosition,
  positionToMoveTo,
  predictionParams,
  acceptedReads = [],
  acceptedReadInFocus,
  rejectedReads = [],
  rejectedReadInFocus,
  showLegend = false,
  showRejectedReads = false,
  onReaderInFocusChange,
  onRejectedReadInFocusChange,
  onAcceptedReadInFocusChange,
}) => {
  const event = useCurrentEvent();

  const shadowTrack = getShadowtrack(event);

  const chunkedShadowtrack = useStore((s) => s.prediction.chunkedShadowTrack);

  const tracks = [];

  shadowTrack &&
    tracks.push(
      simplify(shadowTrack, {
        tolerance: 0.0001,
        highQuality: true,
      }),
    );

  const readerFocusPoint =
    readerInFocus != null && readerInFocus.location != null
      ? [readerInFocus.location.lng, readerInFocus.location.lat]
      : null;

  const acceptedReadFocusPoint =
    acceptedReadInFocus != null
      ? [acceptedReadInFocus.location.lng, acceptedReadInFocus.location.lat]
      : null;

  const rejectedReadFocusPoint =
    rejectedReadInFocus != null
      ? [rejectedReadInFocus.location.lng, rejectedReadInFocus.location.lat]
      : null;

  // Highlights the point selected on the elevation profile on the map
  const hoveredPoint =
    castDraft(
      hoveredPosition != null &&
        chunkedShadowtrack != null && [chunkedShadowtrack.geometry.coordinates[hoveredPosition]],
    ) || [];

  const visuals = getGeoVisuals(readers, acceptedReads, rejectedReads);

  const handleMapItemEnter = (mapLayerMouseEvent: MapMouseEvent) => {
    if (mapLayerMouseEvent.features == null) return;
    for (const feature of mapLayerMouseEvent.features) {
      // the point under the mouse is a reader
      if (
        feature.properties?.readerId != null &&
        typeof feature.properties?.readerId === 'string'
      ) {
        onReaderInFocusChange?.(
          readers.find((r) => r.readerId === feature.properties?.readerId) || null,
        );
      }
      // the point under the mouse is an accepted read
      if (
        feature.properties?.signature != null &&
        typeof feature.properties?.signature === 'string'
      ) {
        onAcceptedReadInFocusChange?.(
          acceptedReads.find((aP) => aP.signature === feature.properties?.signature) || null,
        );
      }

      // the point under the mouse is a rejected read
      if (
        feature.properties?.readerId != null &&
        typeof feature.properties?.signature === 'string'
      ) {
        onRejectedReadInFocusChange?.(
          rejectedReads.find((rP) => rP.signature === feature.properties?.signature) || null,
        );
      }
    }
  };

  const handleMapItemLeave = () => {
    onReaderInFocusChange?.(null);
    onAcceptedReadInFocusChange?.(null);
    onRejectedReadInFocusChange?.(null);
  };

  return (
    <>
      <Container>
        <BasicMap
          controls={{ navigation: true, fitToViewport: true }}
          defaultViewState={{
            ...getDefaultViewState(),
            padding: { top: 100, left: 100, right: 100, bottom: 230 },
          }}
        >
          <FocusPointLayer
            point={positionToMoveTo ? [positionToMoveTo.lng, positionToMoveTo.lat] : null}
            autofocus={AUTOFOCUS.ONCE}
          />
          <Layers.TrackLayer
            tracks={tracks}
            autofocus={AUTOFOCUS.ONCE}
            radius={predictionParams ? predictionParams.distanceFromTrack : 50}
          />
          <Layers.FocusPointLayer
            point={readerFocusPoint || acceptedReadFocusPoint || rejectedReadFocusPoint}
            autofocus={false}
            radius={10}
            fillColor={RacemapColors.AltRed}
          />
          <Layers.TrackLayer
            tracks={visuals.mappingLines}
            color={RacemapColors.Orange}
            lineWidth={2}
          />
          <Layers.PointLayer
            points={visuals.mappingPositions}
            radius={3}
            color={RacemapColors.BaseGreen}
          />
          <Layers.PointLayer
            visible={showRejectedReads}
            points={visuals.rejectedReadsPositions}
            radius={5}
            color={RacemapColors.DarkRed}
            fillColor={RacemapColors.LightRed}
            onMouseEnter={handleMapItemEnter}
            onMouseLeave={handleMapItemLeave}
          />
          <Layers.PointLayer
            points={visuals.acceptedReadsPositions}
            radius={6}
            color={RacemapColors.DarkBlue}
            fillColor={RacemapColors.PaleBlue}
            onMouseEnter={handleMapItemEnter}
            onMouseLeave={handleMapItemLeave}
          />
          <Layers.PointLayer
            points={visuals.readerPositions}
            radius={5}
            color={RacemapColors.BaseRed}
            fillColor="white"
            onMouseEnter={handleMapItemEnter}
            onMouseLeave={handleMapItemLeave}
          />
          <Layers.PointLayer
            points={hoveredPoint}
            radius={2}
            fillColor={RacemapColors.LightOrange}
          />
        </BasicMap>
      </Container>
      {showLegend && (
        <DetailsContainer>
          <span>
            <BlueCircle /> Accepted read
          </span>
          <span>
            <RedCircle /> Rejected read
          </span>

          <span style={{ marginLeft: '14px' }}>
            ReaderId: {acceptedReadInFocus?.readerId || rejectedReadInFocus?.readerId}
          </span>
        </DetailsContainer>
      )}
    </>
  );
};

function BlueCircle() {
  const circleStyle = {
    width: '10px',
    height: '10px',
    backgroundColor: RacemapColors.PaleBlue,
    borderRadius: '50%',
    border: `2px solid ${RacemapColors.DarkBlue}`,
    display: 'inline-block',
  };

  return <div style={circleStyle} />;
}

function RedCircle() {
  const circleStyle = {
    width: '10px',
    height: '10px',
    backgroundColor: RacemapColors.LightRed,
    borderRadius: '50%',
    border: `2px solid ${RacemapColors.DarkRed}`,
    display: 'inline-block',
  };

  return <div style={circleStyle} />;
}

const Container = styled.div`
  grid-area: map;
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const DetailsContainer = styled.div`
  grid-area: map;
  position: relative;
  top: 0;  
  left: 0;
  right: 0;
  height: 84px;
  width: 160px;
  max-width: calc(100% - 50px);
  box-sizing: border-box;
  align-self: start;
  margin: 25px;
  border: solid 0.5px #36739a;
  border-radius: 5px;
  background: #f2e5e540;
  backdrop-filter: blur(10px);
  display: flex;
  padding: 10px;
  flex-direction: column;
  overflow: hidden;
`;
